import { useState } from 'react';
import API from '@/lib/api';
import { Hash, CheckCircle, Warning, MagicWand } from '@phosphor-icons/react';

export default function NummeringPanel() {
  const [rapport, setRapport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const controleer = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await API.get('/admin/controleer-nummering');
      setRapport(data);
    } catch (e) { setMsg('Fout: ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const dryRun = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await API.post('/admin/normaliseer-nummering?dry_run=true');
      setMsg(`Dry-run: ${data.zou_hernoemen} document(en) zouden hernoemd worden (${data.conflicten_opgelost} conflict-oplossingen). Geen wijzigingen uitgevoerd.`);
    } catch (e) { setMsg('Fout: ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const migreer = async () => {
    if (!window.confirm(
      'Weet je zeker dat je alle document-nummers wilt harmoniseren?\n\n' +
      '• Alle oude 3-cijfer IDs worden omgezet naar het huidige padding-formaat.\n' +
      '• Cross-references (bron_id, betalingen, herinneringen) worden mee bijgewerkt.\n' +
      '• Deze actie is onomkeerbaar — maak eerst een database-backup!'
    )) return;
    setBusy(true); setMsg('');
    try {
      const { data } = await API.post('/admin/normaliseer-nummering');
      setMsg(`✓ Klaar! ${data.hernoemd} document(en) hernoemd, ${data.conflicten} conflicten opgelost.`);
      setRapport(null);
    } catch (e) { setMsg('Fout: ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const totaalIssues = (rapport?.inconsistent_count || 0) + (rapport?.duplicaten_count || 0);

  return (
    <div className="section-card" data-testid="nummering-panel">
      <div className="section-header">
        <span className="section-title"><Hash size={16} className="inline mr-1.5" />Document-nummering harmoniseren</span>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-600">
          Wanneer u de <b>padding-instelling</b> (aantal cijfers) aanpast nadat er al documenten bestaan, blijven de oude documenten in het oude formaat staan
          (bv. <code className="px-1 bg-gray-100 rounded">FAC-2026-001</code> naast <code className="px-1 bg-gray-100 rounded">FAC-2026-0001</code>).
          Deze tool harmoniseert alle bestaande nummers naar het huidige padding-formaat.
        </p>

        <div className="flex flex-wrap gap-2">
          <button className="btn btn-sm btn-ghost" disabled={busy} onClick={controleer} data-testid="num-check">
            <MagicWand size={14} /> Controleer nu
          </button>
          {rapport && (
            <>
              <button className="btn btn-sm btn-ghost" disabled={busy || totaalIssues === 0} onClick={dryRun} data-testid="num-dryrun">
                Simulatie
              </button>
              <button className="btn btn-sm btn-primary" disabled={busy || totaalIssues === 0} onClick={migreer} data-testid="num-migreer">
                Harmoniseer nu ({rapport.inconsistent_count})
              </button>
            </>
          )}
        </div>

        {msg && <div className="p-2 text-xs font-medium rounded bg-blue-50 border border-blue-200 text-blue-800">{msg}</div>}

        {rapport && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-gray-50 rounded border border-gray-200 text-center">
                <div className="text-[10px] uppercase text-gray-500 font-bold">Huidig padding</div>
                <div className="text-lg font-bold">{rapport.padding} cijfers</div>
              </div>
              <div className="p-2 rounded border text-center" style={{ background: rapport.inconsistent_count > 0 ? '#FEF3C7' : '#DCFCE7', borderColor: rapport.inconsistent_count > 0 ? '#FCD34D' : '#86EFAC' }}>
                <div className="text-[10px] uppercase font-bold" style={{ color: rapport.inconsistent_count > 0 ? '#92400E' : '#14532D' }}>Inconsistent</div>
                <div className="text-lg font-bold flex items-center justify-center gap-1">
                  {rapport.inconsistent_count > 0 ? <Warning size={16} /> : <CheckCircle size={16} />}
                  {rapport.inconsistent_count}
                </div>
              </div>
              <div className="p-2 rounded border text-center" style={{ background: rapport.duplicaten_count > 0 ? '#FEE2E2' : '#DCFCE7', borderColor: rapport.duplicaten_count > 0 ? '#FCA5A5' : '#86EFAC' }}>
                <div className="text-[10px] uppercase font-bold" style={{ color: rapport.duplicaten_count > 0 ? '#991B1B' : '#14532D' }}>Duplicaten</div>
                <div className="text-lg font-bold flex items-center justify-center gap-1">
                  {rapport.duplicaten_count > 0 ? <Warning size={16} /> : <CheckCircle size={16} />}
                  {rapport.duplicaten_count}
                </div>
              </div>
            </div>

            {rapport.inconsistent_count > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer font-bold text-gray-700 py-1">Toon alle inconsistente nummers ({rapport.inconsistent_count})</summary>
                <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded">
                  <table className="w-full">
                    <thead className="bg-gray-50"><tr><th className="text-left p-2 text-[10px] uppercase text-gray-500">Huidig</th><th className="text-left p-2 text-[10px] uppercase text-gray-500">Wordt</th><th className="text-left p-2 text-[10px] uppercase text-gray-500">Type</th></tr></thead>
                    <tbody>
                      {rapport.inconsistent.map(r => (
                        <tr key={r.id} className="border-t border-gray-100">
                          <td className="p-2 font-mono text-amber-700">{r.id}</td>
                          <td className="p-2 font-mono text-green-700 font-bold">→ {r.zou_worden}</td>
                          <td className="p-2 text-gray-500">{r.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {rapport.duplicaten_count > 0 && (
              <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-800 rounded">
                <b>Let op:</b> {rapport.duplicaten_count} set(s) met dezelfde prefix + jaar + nummer gedetecteerd. De harmonisatie geeft deze een nieuw opvolgnummer zodat uniekheid gegarandeerd blijft.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
